import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { CardSetTransformationService, CoreService, DataService, DialogService, FormConfigurationService, SaveDataRequest, SaveFormEvent, SpinnerService } from 'dspx-core';
import { finalize, take } from 'rxjs';
import { API_ENDPOINTS, API_ENDPOINT_KEYS } from '../../constants/api-endpoints.constants';
import { NAV_TAB_NUMBERS, PAGE_INDEX, PAGE_NUMBERS, POP_UP_CODES, QUESTION_CODES, REFERENCE_DATA_IDS, SAVE_RESULT_STATUS, SECTION_CODES, SECTION_INDEX, SERVER_ERROR_POP_UP_CODES } from '../../constants/app-data.constants';
import { ROUTE_CONSTANTS } from '../../constants/route.constants';
import { TEMPLATES, VERSIONS } from '../../constants/templates.constants';
import { AppDataService } from '../../services/app-data.service';
import { ErrorService } from '../../services/error.service';
import { BaseCardSetDetailComponent } from '../base-card-set-detail-component/base-card-set-detail.component';

@Component({
  selector: 'app-gl-string-mapping',
  templateUrl: './gl-string-mapping.component.html',
  styleUrl: './gl-string-mapping.component.scss'
})
export class GLStringMappingComponent extends BaseCardSetDetailComponent implements OnInit, OnDestroy, AfterViewInit {
  fieldTypeReplacements = CardSetTransformationService.cardSetWithFlatTabsFieldTypeReplacements;
  formServiceEndpoint: string = API_ENDPOINT_KEYS.getFormConfig;

  templateId: number = TEMPLATES.glStringMapping;
  versionNo: number = VERSIONS.latest;
  model: any;
  activeDialog: string = '';
  id: string = '';
  isEditing: boolean = false;

  private saveFormEvent: SaveFormEvent;

  constructor(protected router: Router,
    protected route: ActivatedRoute,
    protected appDataService: AppDataService,
    protected dialogService: DialogService,
    private coreService: CoreService,
    private spinnerService: SpinnerService,
    private dataService: DataService,
    private errorService: ErrorService,
    private formConfigurationService: FormConfigurationService,
  ) {
    super(router, appDataService, dialogService);
  }

  ngOnInit(): void {
    this.coreService.applicationFunctions = {};
    this.coreService.conditionFunctions = {
      isEditing: () => this.isEditing,
      showEntityReturnValue: () => this.showEntityReturnValue(),
      showEntityLookup: () => this.showEntityLookup(),
      showCostCentreReturnValue: () => this.showCostCentreReturnValue(),
      showCostCentreLookup: () => this.showCostCentreLookup(),
      showActivityReturnValue: () => this.showActivityReturnValue(),
      showActivityLookup: () => this.showActivityLookup(),
      showFundReturnValue: () => this.showFundReturnValue(),
      showFundLookup: () => this.showFundLookup(),
      showAccountReturnValue: () => this.showAccountReturnValue(),
      showAccountLookup: () => this.showAccountLookup(),
      showProductTypeReturnValue: () => this.showProductTypeReturnValue(),
      showProductTypeLookup: () => this.showProductTypeLookup(),
      showIdentifierReturnValue: () => this.showIdentifierReturnValue(),
      showIdentifierLookup: () => this.showIdentifierLookup(),
    };

    this.route.paramMap.subscribe((paramMap: ParamMap) => {
      const id = paramMap.get('id');
      if (id) {
        this.id = id;
        this.formServiceEndpoint = API_ENDPOINT_KEYS.getGlStringMappingConfigAndResponse;
        this.isEditing = true;
      }
    });
  }

  ngAfterViewInit(): void {
  }

  ngOnDestroy(): void {
    this.unsubscribeSubscriptions();
    this.coreService.applicationFunctions = {};
    this.coreService.repeatingPageFunctions = {};
    this.coreService.repeatingSectionFunctions = {};
  }

  formLoaded(event: any): void {
  }

  modelChanged(event: any): void {
  }

  filter(event: any): void {
  }

  buttonClicked(event: any): void {
  }

  save(event: SaveFormEvent) {
    if (
      this.baseForm?.model?.form?.tabs != null
      && this.getTransactionTypeId() != null
      && this.getStartDate() != null
    ) {
      this.spinnerService.showSpinner();
      this.registerSubscription(
        this.appDataService
          .post(
            API_ENDPOINTS.glStringMapping.checkForOverlaps,
            {
              glStringMappingId: this.id,
              transactionTypeId: this.getTransactionTypeId(),
              startDate: this.getStartDate(),
              endDate: this.getEndDate()
            }
          )
          .pipe(
            take(1),
            this.errorService.errorHandler(),
            finalize(() => this.spinnerService.hideSpinner())
          )
          .subscribe(data => {
            if (data?.status === SAVE_RESULT_STATUS.success) {
              if (SERVER_ERROR_POP_UP_CODES.glStringMappingOverlapCheck.includes(data.message)) {
                this.activeDialog = data.message;
                this.saveFormEvent = event;
                this.dialogService.showDialog(this.activeDialog);
              }
              else {
                this.completeSave(event);
              }
            }
          })
      );
    }
    else {
      this.completeSave(event);
    }
  }

  cancel(event: any) {
    this.exit();
  }

  dialogButton_confirmOverlappingMapping_Clicked(event: any) {
    this.completeSave(this.saveFormEvent);
  }

  dialogButton_saveSuccessful_Clicked(event: any) {
    this.exit(true);
  }

  private exit(ignoreUnsavedChanges = false): void {
    this.navigate(['/', ROUTE_CONSTANTS.glStringMapping.list], ignoreUnsavedChanges);
  }

  private completeSave(event: SaveFormEvent) {
    const saveDataRequest: SaveDataRequest = {
      template: this.templateId,
      versionNumber: this.versionNo,
      id: this.id,
      response: event.model,
      expectedLastUpdateTime: event.lastUpdatedTime,
      isSubmit: event.isSubmit,
    }
    if (!event.isFormValid) {
      this.baseForm.markAllFieldsAsTouched();
      if (!this.baseForm.fields[0]?.formControl.valid) {
        this.baseForm.showValidationSummary(true);
        this.activeDialog = POP_UP_CODES.common.validationError;
        this.dialogService.showDialog(POP_UP_CODES.common.validationError);
      }
      else {
        this.saveData(saveDataRequest);
      }
    }
    else {
      this.saveData(saveDataRequest);
    }
  }

  private saveData(request: SaveDataRequest) {
    this.spinnerService.showSpinner();
    this.registerSubscription(
      this.dataService.saveData(API_ENDPOINTS.glStringMapping.save, request)
        .pipe(
          take(1),
          this.errorService.errorHandler(),
          finalize(() => this.spinnerService.hideSpinner())
        )
        .subscribe(data => {
          if (data?.status === SAVE_RESULT_STATUS.success) {
            if (SERVER_ERROR_POP_UP_CODES.saveGlStringMapping.includes(data.message)) {
              this.activeDialog = data.message;
            }
            else {
              this.id = data.id;
              this.activeDialog = POP_UP_CODES.common.saveSuccessful;
            }
            this.dialogService.showDialog(this.activeDialog);
          }
        })
    );
  }

  private getTransactionTypeId(): string {
    return this.formConfigurationService.getQuestionResponse(
      this.baseForm.model.form.tabs,
      'tab' + NAV_TAB_NUMBERS.glStringMappingForm.glStringMapping,
      'page' + PAGE_NUMBERS.glStringMappingForm.glStringMapping,
      PAGE_INDEX.noRepeater,
      SECTION_CODES.glStringMappingForm.mappingSection,
      SECTION_INDEX.noRepeater,
      QUESTION_CODES.glStringMappingForm.transactionTypeId
    );
  }

  private getEntityValueTypeId(): string {
    return this.formConfigurationService.getQuestionResponse(
      this.baseForm.model.form.tabs,
      'tab' + NAV_TAB_NUMBERS.glStringMappingForm.glStringMapping,
      'page' + PAGE_NUMBERS.glStringMappingForm.glStringMapping,
      PAGE_INDEX.noRepeater,
      SECTION_CODES.glStringMappingForm.mappingSection,
      SECTION_INDEX.noRepeater,
      QUESTION_CODES.glStringMappingForm.entityValueTypeId
    );
  }

  private getCostCentreValueTypeId(): string {
    return this.formConfigurationService.getQuestionResponse(
      this.baseForm.model.form.tabs,
      'tab' + NAV_TAB_NUMBERS.glStringMappingForm.glStringMapping,
      'page' + PAGE_NUMBERS.glStringMappingForm.glStringMapping,
      PAGE_INDEX.noRepeater,
      SECTION_CODES.glStringMappingForm.mappingSection,
      SECTION_INDEX.noRepeater,
      QUESTION_CODES.glStringMappingForm.costCentreValueTypeId
    );
  }

  private getActivityValueTypeId(): string {
    return this.formConfigurationService.getQuestionResponse(
      this.baseForm.model.form.tabs,
      'tab' + NAV_TAB_NUMBERS.glStringMappingForm.glStringMapping,
      'page' + PAGE_NUMBERS.glStringMappingForm.glStringMapping,
      PAGE_INDEX.noRepeater,
      SECTION_CODES.glStringMappingForm.mappingSection,
      SECTION_INDEX.noRepeater,
      QUESTION_CODES.glStringMappingForm.activityValueTypeId
    );
  }

  private getFundValueTypeId(): string {
    return this.formConfigurationService.getQuestionResponse(
      this.baseForm.model.form.tabs,
      'tab' + NAV_TAB_NUMBERS.glStringMappingForm.glStringMapping,
      'page' + PAGE_NUMBERS.glStringMappingForm.glStringMapping,
      PAGE_INDEX.noRepeater,
      SECTION_CODES.glStringMappingForm.mappingSection,
      SECTION_INDEX.noRepeater,
      QUESTION_CODES.glStringMappingForm.fundValueTypeId
    );
  }

  private getAccountValueTypeId(): string {
    return this.formConfigurationService.getQuestionResponse(
      this.baseForm.model.form.tabs,
      'tab' + NAV_TAB_NUMBERS.glStringMappingForm.glStringMapping,
      'page' + PAGE_NUMBERS.glStringMappingForm.glStringMapping,
      PAGE_INDEX.noRepeater,
      SECTION_CODES.glStringMappingForm.mappingSection,
      SECTION_INDEX.noRepeater,
      QUESTION_CODES.glStringMappingForm.accountValueTypeId
    );
  }

  private getProductTypeValueTypeId(): string {
    return this.formConfigurationService.getQuestionResponse(
      this.baseForm.model.form.tabs,
      'tab' + NAV_TAB_NUMBERS.glStringMappingForm.glStringMapping,
      'page' + PAGE_NUMBERS.glStringMappingForm.glStringMapping,
      PAGE_INDEX.noRepeater,
      SECTION_CODES.glStringMappingForm.mappingSection,
      SECTION_INDEX.noRepeater,
      QUESTION_CODES.glStringMappingForm.productTypeValueTypeId
    );
  }

  private getIdentifierValueTypeId(): string {
    return this.formConfigurationService.getQuestionResponse(
      this.baseForm.model.form.tabs,
      'tab' + NAV_TAB_NUMBERS.glStringMappingForm.glStringMapping,
      'page' + PAGE_NUMBERS.glStringMappingForm.glStringMapping,
      PAGE_INDEX.noRepeater,
      SECTION_CODES.glStringMappingForm.mappingSection,
      SECTION_INDEX.noRepeater,
      QUESTION_CODES.glStringMappingForm.identifierValueTypeId
    );
  }

  private getStartDate(): string {
    return this.formConfigurationService.getQuestionResponse(
      this.baseForm.model.form.tabs,
      'tab' + NAV_TAB_NUMBERS.glStringMappingForm.glStringMapping,
      'page' + PAGE_NUMBERS.glStringMappingForm.glStringMapping,
      PAGE_INDEX.noRepeater,
      SECTION_CODES.glStringMappingForm.mappingSection,
      SECTION_INDEX.noRepeater,
      QUESTION_CODES.glStringMappingForm.startDate
    );
  }

  private getEndDate(): string {
    return this.formConfigurationService.getQuestionResponse(
      this.baseForm.model.form.tabs,
      'tab' + NAV_TAB_NUMBERS.glStringMappingForm.glStringMapping,
      'page' + PAGE_NUMBERS.glStringMappingForm.glStringMapping,
      PAGE_INDEX.noRepeater,
      SECTION_CODES.glStringMappingForm.mappingSection,
      SECTION_INDEX.noRepeater,
      QUESTION_CODES.glStringMappingForm.endDate
    );
  }

  private showEntityReturnValue(): boolean {
    if (this.baseForm?.model?.form?.tabs != null) {
      const entityValueTypeId = this.getEntityValueTypeId();

      return entityValueTypeId === REFERENCE_DATA_IDS.glStringSegmentValueType.segment;
    }

    return false;
  }

  private showEntityLookup(): boolean {
    if (this.baseForm?.model?.form?.tabs != null) {
      const entityValueTypeId = this.getEntityValueTypeId();

      return entityValueTypeId === REFERENCE_DATA_IDS.glStringSegmentValueType.lookup;
    }

    return false;
  }

  private showCostCentreReturnValue(): boolean {
    if (this.baseForm?.model?.form?.tabs != null) {
      const entityValueTypeId = this.getCostCentreValueTypeId();

      return entityValueTypeId === REFERENCE_DATA_IDS.glStringSegmentValueType.segment;
    }

    return false;
  }

  private showCostCentreLookup(): boolean {
    if (this.baseForm?.model?.form?.tabs != null) {
      const entityValueTypeId = this.getCostCentreValueTypeId();

      return entityValueTypeId === REFERENCE_DATA_IDS.glStringSegmentValueType.lookup;
    }

    return false;
  }

  private showActivityReturnValue(): boolean {
    if (this.baseForm?.model?.form?.tabs != null) {
      const entityValueTypeId = this.getActivityValueTypeId();

      return entityValueTypeId === REFERENCE_DATA_IDS.glStringSegmentValueType.segment;
    }

    return false;
  }

  private showActivityLookup(): boolean {
    if (this.baseForm?.model?.form?.tabs != null) {
      const entityValueTypeId = this.getActivityValueTypeId();

      return entityValueTypeId === REFERENCE_DATA_IDS.glStringSegmentValueType.lookup;
    }

    return false;
  }

  private showFundReturnValue(): boolean {
    if (this.baseForm?.model?.form?.tabs != null) {
      const entityValueTypeId = this.getFundValueTypeId();

      return entityValueTypeId === REFERENCE_DATA_IDS.glStringSegmentValueType.segment;
    }

    return false;
  }

  private showFundLookup(): boolean {
    if (this.baseForm?.model?.form?.tabs != null) {
      const entityValueTypeId = this.getFundValueTypeId();

      return entityValueTypeId === REFERENCE_DATA_IDS.glStringSegmentValueType.lookup;
    }

    return false;
  }

  private showAccountReturnValue(): boolean {
    if (this.baseForm?.model?.form?.tabs != null) {
      const entityValueTypeId = this.getAccountValueTypeId();

      return entityValueTypeId === REFERENCE_DATA_IDS.glStringSegmentValueType.segment;
    }

    return false;
  }

  private showAccountLookup(): boolean {
    if (this.baseForm?.model?.form?.tabs != null) {
      const entityValueTypeId = this.getAccountValueTypeId();

      return entityValueTypeId === REFERENCE_DATA_IDS.glStringSegmentValueType.lookup;
    }

    return false;
  }

  private showProductTypeReturnValue(): boolean {
    if (this.baseForm?.model?.form?.tabs != null) {
      const entityValueTypeId = this.getProductTypeValueTypeId();

      return entityValueTypeId === REFERENCE_DATA_IDS.glStringSegmentValueType.segment;
    }

    return false;
  }

  private showProductTypeLookup(): boolean {
    if (this.baseForm?.model?.form?.tabs != null) {
      const entityValueTypeId = this.getProductTypeValueTypeId();

      return entityValueTypeId === REFERENCE_DATA_IDS.glStringSegmentValueType.lookup;
    }

    return false;
  }

  private showIdentifierReturnValue(): boolean {
    if (this.baseForm?.model?.form?.tabs != null) {
      const entityValueTypeId = this.getIdentifierValueTypeId();

      return entityValueTypeId === REFERENCE_DATA_IDS.glStringSegmentValueType.segment;
    }

    return false;
  }

  private showIdentifierLookup(): boolean {
    if (this.baseForm?.model?.form?.tabs != null) {
      const entityValueTypeId = this.getIdentifierValueTypeId();

      return entityValueTypeId === REFERENCE_DATA_IDS.glStringSegmentValueType.lookup;
    }

    return false;
  }
}
